
import React, { useState, useEffect } from 'react';
import { X, Check, Crown, Zap, Clock, ShieldCheck, CreditCard, Star, Plus, Copy, CheckCircle, Loader2, FileText, XCircle } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { UserProfile } from '../types';

interface SubscriptionModalProps {
    isOpen: boolean;
    onClose: () => void;
    user: UserProfile | null;
    onSuccess: () => void;
    featureName?: string; // Optional feature name that triggered the paywall
}

export const SubscriptionModal: React.FC<SubscriptionModalProps> = ({ isOpen, onClose, user, onSuccess, featureName }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    const [pixData, setPixData] = useState<{ qr_code: string; qr_code_base64: string; ticket_url: string } | null>(null);
    const [copied, setCopied] = useState(false);
    const [deviceId, setDeviceId] = useState<string | null>(null);
    const [pixPollingStatus, setPixPollingStatus] = useState<'idle' | 'checking' | 'confirmed'>('idle');


    // Load Mercado Pago device fingerprint script for better payment approval rates
    useEffect(() => {
        if (!isOpen) return;

        const scriptId = 'mp-security-sdk';
        let script = document.getElementById(scriptId) as HTMLScriptElement | null;

        const captureDeviceId = () => {
            // MP security script sets window.MP_DEVICE_SESSION_ID after loading
            const id = (window as any).MP_DEVICE_SESSION_ID;
            if (id) {
                setDeviceId(id);
                console.log('[MP] Device ID captured:', id);
            }
        };

        if (!script) {
            script = document.createElement('script');
            script.id = scriptId;
            script.src = 'https://www.mercadopago.com/v2/security.js';
            script.setAttribute('view', 'checkout');
            script.onload = () => setTimeout(captureDeviceId, 500);
            document.head.appendChild(script);
        } else {
            // Script already loaded, just try to capture
            setTimeout(captureDeviceId, 300);
        }
    }, [isOpen]);

    useEffect(() => {
        const targetDate = new Date('2026-05-01T23:59:59-03:00').getTime();

        const calculateTimeLeft = () => {
            const now = new Date().getTime();
            const difference = targetDate - now;

            if (difference > 0) {
                setTimeLeft({
                    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
                    hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
                    minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
                    seconds: Math.floor((difference % (1000 * 60)) / 1000)
                });
            } else {
                setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
            }
        };

        if (isOpen) {
             calculateTimeLeft();
             const timer = setInterval(calculateTimeLeft, 1000);
             return () => clearInterval(timer);
        }
    }, [isOpen]);

    // Auto-poll Mercado Pago every 15s while QR code is displayed
    useEffect(() => {
        if (!pixData || !user) return;

        setPixPollingStatus('idle');
        let cancelled = false;

        const poll = async () => {
            if (cancelled) return;
            setPixPollingStatus('checking');
            try {
                const { data } = await supabase.functions.invoke('check-pix-payment', {
                    body: { userId: user.id }
                });
                if (cancelled) return;
                if (data?.success) {
                    setPixPollingStatus('confirmed');
                    setTimeout(() => {
                        onSuccess();
                        onClose();
                    }, 1500);
                    return; // Stop polling
                }
            } catch {
                // Silent — will retry
            }
            if (!cancelled) setPixPollingStatus('idle');
        };

        // First check after 10s (give MP time to process)
        const firstTimer = setTimeout(poll, 10_000);
        // Then every 15s
        const interval = setInterval(poll, 15_000);

        return () => {
            cancelled = true;
            clearTimeout(firstTimer);
            clearInterval(interval);
        };
    }, [pixData, user, onSuccess, onClose]);

    if (!isOpen) return null;

    const handlePayment = async (method: 'subscription' | 'pix') => {
        if (!user) return;
        setLoading(true);
        setError(null);
        setPixData(null);

        try {
            // Refresh device ID just before payment in case it loaded after modal opened
            const rawId = deviceId || (window as any).MP_DEVICE_SESSION_ID;
            const latestDeviceId = typeof rawId === 'string' && rawId.trim().length > 0 ? rawId.trim() : null;

            // Sanitize phone: remove all non-digit characters (e.g. +55 → 55) to build a valid MP email
            const cleanPhone = user.phone ? user.phone.replace(/\D/g, '') : '';
            
            // Prioritize user's actual email, fallback to phone-based email or user id email
            const payerEmail = user.email || (cleanPhone
                ? `${cleanPhone}@driverscore.com`
                : `user${user.id.replace(/-/g, '')}@driverscore.com`);

            const { data, error: funcError } = await supabase.functions.invoke('process-subscription', {
                body: {
                    userId: user.id,
                    email: payerEmail,
                    paymentMethod: method,
                    deviceId: latestDeviceId
                }
            });

            if (funcError) {
                let errorMessage = 'Erro ao processar pagamento.';
                if (funcError.context) {
                    try {
                        const body = await funcError.context.json();
                        if (body?.error) {
                            errorMessage = body.error;
                        } else if (body?.message) {
                            errorMessage = body.message;
                        }
                    } catch {
                        try {
                            const text = await funcError.context.text();
                            if (text) errorMessage = text;
                        } catch {}
                    }
                } else {
                    errorMessage = funcError.message || errorMessage;
                }
                throw new Error(errorMessage);
            }

            if (method === 'pix' && data?.qr_code) {
                // Show Pix QR Code in-app
                setPixData({
                    qr_code: data.qr_code,
                    qr_code_base64: data.qr_code_base64,
                    ticket_url: data.ticket_url
                });
            } else if (data?.init_point) {
                // Redirect to Mercado Pago for card subscription
                window.location.href = data.init_point;
            } else {
                throw new Error('Não foi possível gerar o pagamento.');
            }
        } catch (err: any) {
            console.error('Payment error:', err);
            setError(err.message || 'Erro ao processar pagamento.');
        } finally {
            setLoading(false);
        }
    };

    const handleCheckPix = async () => {
        if (!user) return;
        setLoading(true);
        setError(null);

        try {
            const { data, error: funcError } = await supabase.functions.invoke('check-pix-payment', {
                body: { userId: user.id }
            });

            if (funcError) throw funcError;

            if (data?.success) {
                setPixPollingStatus('confirmed');
                setTimeout(() => {
                    onSuccess();
                    onClose();
                }, 1000);
            } else {
                setError(data?.message || 'Pagamento ainda não confirmado. Aguarde alguns instantes ou verifique seu extrato.');
            }
        } catch (err: any) {
            console.error('Check Pix error:', err);
            setError(err.message || 'Erro ao verificar pagamento.');
        } finally {
            setLoading(false);
        }
    };

    const handleCopyPix = async () => {
        if (!pixData?.qr_code) return;
        try {
            await navigator.clipboard.writeText(pixData.qr_code);
            setCopied(true);
            setTimeout(() => setCopied(false), 3000);
        } catch {
            // Fallback for older browsers
            const textarea = document.createElement('textarea');
            textarea.value = pixData.qr_code;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
            setCopied(true);
            setTimeout(() => setCopied(false), 3000);
        }
    };

    const createdAt = user?.created_at ? new Date(user.created_at) : null;
    const daysSinceCreation = createdAt ? (new Date().getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24) : 0;
    const isReferralActive = user?.user_metadata?.referred_by && daysSinceCreation <= 30;
    const daysLeftForBonus = Math.max(0, Math.ceil(30 - daysSinceCreation));

    const isBeforeMay1 = new Date().getTime() < new Date('2026-05-01T23:59:59-03:00').getTime();
    const displayPrice = "10,90";
    const showSweepstakes = isBeforeMay1 && !user?.is_founder;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300">
            <div className="bg-slate-900 border border-slate-800 w-full max-w-md rounded-3xl overflow-hidden shadow-2xl relative">
                {/* Header with Background Pattern */}
                <div className="bg-gradient-to-br from-amber-500/20 to-orange-600/20 p-8 pb-8 relative">
                    <div className="absolute top-0 right-0 p-4">
                        <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
                            <X size={24} />
                        </button>
                    </div>

                    <div className="flex flex-col items-center text-center space-y-3">
                        <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg shadow-orange-500/20">
                            <Crown size={32} className="text-slate-900" />
                        </div>
                        <div className="pt-1">
                            {featureName && (
                                <div className="inline-flex items-center gap-1.5 bg-orange-500/20 border border-orange-500/30 text-orange-300 text-xs font-bold px-3 py-1 rounded-full mb-3">
                                    <Star size={12} className="fill-current animate-pulse" /> Libere: {featureName}
                                </div>
                            )}
                            <h2 className="text-2xl font-black text-white tracking-tight leading-tight">
                                Proteja o seu suado dinheiro.
                            </h2>
                            <p className="text-slate-300 text-xs sm:text-sm font-normal mt-2 leading-relaxed max-w-sm mx-auto">
                                Diante das dificuldades e descontos abusivos que nós, motoristas, temos enfrentado todas as semanas, o Driver Score reduziu o valor para apoiar a categoria.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Features List */}
                <div className="p-8 pt-6 space-y-6 bg-slate-900 overflow-y-auto max-h-[60vh] scrollbar-hide">
                    
                    {user?.is_founder && (
                        <div className="bg-amber-500/10 border border-amber-500/30 p-3 rounded-xl flex items-center gap-3 relative overflow-hidden text-center justify-center mb-4">
                            <Star className="text-amber-500 w-5 h-5 flex-shrink-0" />
                            <span className="text-amber-500/90 font-bold text-xs">Membro Fundador Ativo: Seu preço de R$ 10,90 foi garantido.</span>
                        </div>
                    )}

                    {/* Why Box-Preta Header */}
                    <div className="text-center">
                        <h3 className="text-xs font-extrabold text-orange-400 uppercase tracking-widest">
                            Por que ter a sua "Caixa-Preta"?
                        </h3>
                    </div>

                    {/* Benefits List */}
                    <div className="space-y-4 pt-1">
                        {/* Benefit 1 */}
                        <div className="flex items-start gap-3.5 group">
                            <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center shrink-0 group-hover:bg-cyan-500/20 transition-all border border-cyan-500/10">
                                <FileText className="text-cyan-400" size={20} />
                            </div>
                            <div>
                                <h4 className="text-white font-bold text-sm">
                                    📑 Auditoria Automatizada
                                </h4>
                                <p className="text-slate-400 text-xs leading-relaxed mt-0.5">
                                    Registre seus horários exatos de login, logout e turnos sem depender do Deputy.
                                </p>
                            </div>
                        </div>

                        {/* Benefit 2 */}
                        <div className="flex items-start gap-3.5 group">
                            <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0 group-hover:bg-amber-500/20 transition-all border border-amber-500/10">
                                <ShieldCheck className="text-amber-400" size={20} />
                            </div>
                            <div>
                                <h4 className="text-white font-bold text-sm">
                                    🛡️ Proteção Contra a Pejotização
                                </h4>
                                <p className="text-slate-400 text-xs leading-relaxed mt-0.5">
                                    Tenha o registro histórico e incontestável do seu tempo à disposição, provando a sua jornada real caso precise de amparo jurídico.
                                </p>
                            </div>
                        </div>

                        {/* Benefit 3 */}
                        <div className="flex items-start gap-3.5 group">
                            <div className="w-10 h-10 rounded-xl bg-rose-500/10 flex items-center justify-center shrink-0 group-hover:bg-rose-500/20 transition-all border border-rose-500/10">
                                <XCircle className="text-rose-400" size={20} />
                            </div>
                            <div>
                                <h4 className="text-white font-bold text-sm">
                                    ❌ Fim dos Descontos Injustos
                                </h4>
                                <p className="text-slate-400 text-xs leading-relaxed mt-0.5">
                                    Acumule provas técnicas contra falsos positivos do algoritmo, como erros de GPS, Long Finishing e Ponto A.
                                </p>
                            </div>
                        </div>

                        {isReferralActive && (
                            <div className="mt-4 p-4 bg-gradient-to-r from-blue-600/20 to-indigo-600/20 border border-blue-500/30 rounded-2xl animate-pulse">
                                <div className="flex items-center gap-3 mb-1">
                                    <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                                    <h4 className="text-white font-bold text-sm">BÔNUS DE INDICAÇÃO ATIVO!</h4>
                                </div>
                                <p className="text-blue-100 text-xs leading-relaxed">
                                    Assine em até <span className="font-bold text-white underline">{daysLeftForBonus} dias</span> para ganhar <span className="text-yellow-400 font-bold">3 MESES GRÁTIS</span> extras! 🎁
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Pricing Block */}
                    <div className="bg-slate-800/40 border border-slate-700/40 rounded-2xl p-4 text-center space-y-2">
                        <p className="text-amber-400 text-[11px] font-extrabold uppercase tracking-widest">
                            A nossa força é a nossa união.
                        </p>
                        <div className="flex items-center justify-center gap-2">
                            <span className="text-slate-500 line-through text-sm sm:text-base font-semibold">
                                De R$ 16,90
                            </span>
                            <span className="text-white font-medium text-sm sm:text-base">
                                por apenas
                            </span>
                            <span className="text-2xl font-black text-emerald-400">
                                R$ 10,90<span className="text-xs text-slate-400 font-normal">/mês</span>
                            </span>
                        </div>
                        <p className="text-slate-400 text-[10px] sm:text-xs">
                            (Valor unificado para todos os usuários)
                        </p>
                    </div>

                    {/* Pix Highlight */}
                    <div className="bg-emerald-500/5 border border-emerald-500/15 p-3 rounded-xl flex items-center gap-3">
                        <Zap className="text-emerald-400 fill-emerald-400/20 shrink-0 animate-pulse" size={18} />
                        <p className="text-emerald-400/90 text-xs font-semibold leading-snug">
                            Liberação instantânea pagando via <span className="underline">Pix</span>!
                        </p>
                    </div>

                    {error && (
                        <div className="p-3 bg-red-500/10 border border-red-500/20 text-red-500 text-sm rounded-xl text-center">
                            {error}
                        </div>
                    )}

                    {/* Pix QR Code View */}
                    {pixData ? (
                        <div className="flex flex-col items-center gap-4">

                            {pixPollingStatus === 'confirmed' ? (
                                <div className="flex flex-col items-center gap-3 py-6 animate-in fade-in duration-300">
                                    <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center">
                                        <CheckCircle size={44} className="text-emerald-400" />
                                    </div>
                                    <h3 className="text-white font-bold text-lg">Pagamento confirmado! 🎉</h3>
                                    <p className="text-emerald-400 text-sm text-center">Seu acesso Premium está sendo liberado...</p>
                                </div>
                            ) : (
                                <>
                                    {/* Auto-detection status bar */}
                                    <div className={`w-full flex items-center justify-center gap-2 py-2 px-3 rounded-xl text-xs font-medium transition-all ${
                                        pixPollingStatus === 'checking'
                                            ? 'bg-blue-500/10 border border-blue-500/20 text-blue-400'
                                            : 'bg-slate-800/50 border border-slate-700/50 text-slate-500'
                                    }`}>
                                        {pixPollingStatus === 'checking' ? (
                                            <><Loader2 size={12} className="animate-spin" /> Verificando pagamento automaticamente...</>
                                        ) : (
                                            <><div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> Aguardando confirmação do Pix...</>
                                        )}
                                    </div>

                                    <div className="bg-white p-4 rounded-2xl shadow-lg">
                                        <img 
                                            src={`data:image/png;base64,${pixData.qr_code_base64}`} 
                                            alt="QR Code Pix" 
                                            className="w-48 h-48"
                                        />
                                    </div>
                                    
                                    <p className="text-emerald-400 text-sm font-semibold text-center">Escaneie o QR Code acima ou copie o código Pix:</p>

                                    <button
                                        onClick={handleCopyPix}
                                        className={`w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-bold text-sm transition-all active:scale-[0.97] ${
                                            copied 
                                                ? 'bg-emerald-500/20 border border-emerald-500/40 text-emerald-400'
                                                : 'bg-slate-800 border border-slate-700 hover:border-emerald-500/50 text-white'
                                        }`}
                                    >
                                        {copied ? (
                                            <><CheckCircle size={18} /> Código copiado!</>
                                        ) : (
                                            <><Copy size={18} /> <span className="text-sm font-bold">Copiar Pix Copia e Cola</span></>
                                        )}
                                    </button>

                                    <button
                                        onClick={handleCheckPix}
                                        disabled={loading || pixPollingStatus === 'checking'}
                                        className="w-full bg-white/10 hover:bg-white/20 text-white font-bold py-3 px-6 rounded-2xl border border-white/10 transition-all flex items-center justify-center gap-2 mt-2 disabled:opacity-50"
                                    >
                                        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle size={18} className="text-emerald-400" />}
                                        Já paguei — verificar agora
                                    </button>

                                    <p className="text-slate-500 text-xs text-center leading-relaxed">
                                        O sistema verifica automaticamente. Ou clique no botão acima para checar na hora. O QR Code expira em 30 minutos.
                                    </p>

                                    <button 
                                        onClick={() => { setPixData(null); setPixPollingStatus('idle'); }}
                                        className="text-slate-500 text-xs underline hover:text-slate-300 transition-colors"
                                    >
                                        ← Voltar às opções de pagamento
                                    </button>
                                </>
                            )}
                        </div>
                    ) : (
                        <>
                            <div className="flex flex-col gap-3">
                                <button
                                    onClick={() => handlePayment('pix')}
                                    disabled={loading}
                                    className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 active:scale-[0.98] text-white font-bold py-4 px-6 rounded-2xl shadow-xl shadow-emerald-600/20 transition-all flex items-center justify-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? (
                                        <span className="flex items-center gap-2">
                                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                            Gerando Pix...
                                        </span>
                                    ) : (
                                        <>
                                            ASSINAR AGORA E ME PROTEGER <Zap size={20} className="fill-current" />
                                        </>
                                    )}
                                </button>

                                <button
                                    onClick={() => handlePayment('subscription')}
                                    disabled={loading}
                                    className="w-full bg-slate-800 border border-slate-700 hover:border-amber-500/50 active:scale-[0.98] text-white font-semibold py-3.5 px-6 rounded-2xl shadow-lg transition-all flex items-center justify-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? (
                                        <span className="flex items-center gap-2">
                                            <div className="w-5 h-5 border-2 border-slate-500 border-t-slate-300 rounded-full animate-spin" />
                                            Processando...
                                        </span>
                                    ) : (
                                        <>
                                            Assinatura Mensal (Cartão) <CreditCard size={18} className="text-slate-400 group-hover:text-amber-500 transition-colors" />
                                        </>
                                    )}
                                </button>
                            </div>

                            <p className="text-center text-slate-500 text-xs px-4">
                                Pix: pagamento único, válido por 30 dias. Cartão: renovação automática mensal.
                            </p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
